import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { prospectDate } from '@/utils/prospectTime';
import { ProspectReplyButton } from '@/components/admin/ProspectReply';
import { MeetingOutcome, ProposeDateButton, ArchiveRescheduleButton, ConfirmManualButton, meetingPassed } from '@/components/admin/MeetingOutcome';

const HaltClear = ({ p, onChanged }) => {
  const clear = async () => {
    if (!window.confirm('Reactivar a ficha? As faltas voltam a zero e os emails automáticos voltam a ser enviados.')) return;
    try { await api.post(`/admin/prospects/${p.id}/halt/clear`); toast.success('Ficha reactivada'); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };
  return <button type="button" onClick={clear} className="ml-1 underline font-normal" data-testid={`halt-clear-${p.id}`}>reactivar</button>;
};

export const STAGE_TABS = [
  { key: 'to_contact', label: '🗂️ Por contactar' },
  { key: 'contacted', label: '📤 Contactado' },
  { key: 'interested', label: '👀 Interessado' },
  { key: 'preference', label: '💬 Preferência escolhida' },
  { key: 'invite_sent', label: '⏳ Convite enviado' },
  { key: 'confirmed', label: '✅ Reunião confirmada' },
  { key: 'held', label: '📝 Reunião realizada' },
  { key: 'pioneer', label: '⭐ Oferta Pioneiro / Activada' },
  { key: 'declined', label: '❌ Sem interesse / Recusou' },
];
const LABEL = Object.fromEntries(STAGE_TABS.map((s) => [s.key, s.label]));
export const stageOf = (p) => p.stage || 'to_contact';

// Etapas em que há algo a fazer pelo admin; aviso ao fim de 5 dias sem mudança
const STALL_STAGES = ['interested', 'preference', 'invite_sent', 'held'];
const STALL_DAYS = 5;
export const daysInStage = (p) => { const d = prospectDate(p.stage_at); return d ? Math.floor((Date.now() - d) / 86400000) : 0; };
export const isStalled = (p) => Boolean(p.halted) || (STALL_STAGES.includes(stageOf(p)) && daysInStage(p) > STALL_DAYS);

export const StageTabs = ({ value, onChange, counts, total }) => (
  <div className="mb-6 -mx-4 px-4 overflow-x-auto" data-testid="prospect-stage-tabs">
    <div className="flex gap-1 border-b border-slate-200 min-w-max">
      {[{ key: '', label: 'Todas' }, { key: 'stalled', label: '⚠️ Paradas' }, ...STAGE_TABS].map((s) => (
        <button key={s.key || 'all'} type="button" onClick={() => onChange(s.key)}
          className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${value === s.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
          data-testid={`prospect-stage-tab-${s.key || 'all'}`}>
          {s.label} <span className="ml-1 rounded-full bg-slate-100 px-1.5 text-xs text-slate-700">{s.key ? counts[s.key] || 0 : total}</span>
        </button>
      ))}
    </div>
  </div>
);

const fmtTz = (d, tz) => d.toLocaleString('pt-PT', { timeZone: tz, weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
export const MeetingTimes = ({ p }) => {
  const d = prospectDate(p.meeting?.start_at);
  if (!d) return null;
  return <p className="text-xs text-emerald-800 mt-1" data-testid={`prospect-meeting-times-${p.id}`}>📅 {fmtTz(d, 'Atlantic/Cape_Verde')} (Cabo Verde) · {d.toLocaleTimeString('pt-PT', { timeZone: 'Europe/Zurich', hour: '2-digit', minute: '2-digit' })} (Suíça)</p>;
};

const useStage = (p, onChanged) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); } finally { setBusy(false); }
  };
  const setStage = (stage, ok = 'Etapa actualizada') => run(() => api.post(`/admin/prospects/${p.id}/stage`, { stage }), ok);
  return { busy, run, setStage };
};

export const StageSelect = ({ p, onChanged }) => {
  const { busy, setStage } = useStage(p, onChanged);
  return (
    <select value={stageOf(p)} disabled={busy} onChange={(e) => setStage(e.target.value)} title="Mudar etapa à mão"
      className="text-[11px] font-bold rounded-full px-2 py-0.5 border border-slate-200 bg-white" data-testid={`prospect-stage-select-${p.id}`}>
      {STAGE_TABS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
    </select>
  );
};

const PioneerAction = ({ p, onChanged }) => {
  const navigate = useNavigate();
  const { busy, run, setStage } = useStage(p, onChanged);
  if (p.linked_company_id) {
    return <Button size="sm" onClick={() => navigate(`/admin/companies?search=${encodeURIComponent(p.linked_company_email || p.linked_company_name || p.email || p.company)}`)} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid={`stage-action-pioneer-${p.id}`}>⭐ Enviar Oferta Pioneiro</Button>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span className="text-xs font-semibold text-amber-800" data-testid={`stage-not-registered-${p.id}`}>Empresa ainda não registada</span>
      <Button size="sm" variant="outline" disabled={busy || !p.email} onClick={() => run(() => api.post(`/admin/prospects/${p.id}/registration-invite`), 'Convite de registo enviado')} data-testid={`stage-action-register-invite-${p.id}`}>
        {p.registration_invite_sent_at ? 'Reenviar convite de registo' : 'Enviar convite de registo'}
      </Button>
      <button type="button" disabled={busy} onClick={() => setStage('pioneer', 'Marcada como Oferta enviada')} className="text-xs text-slate-500 underline" data-testid={`stage-action-mark-offer-${p.id}`}>Marcar como Oferta enviada</button>
    </span>
  );
};

export const NextAction = ({ p, onChanged, onCompose }) => {
  const navigate = useNavigate();
  const stage = stageOf(p);
  const clickInvite = () => document.querySelector(`[data-testid="prospect-invite-${p.id}"]`)?.click();
  const stalled = isStalled(p);
  const wrap = (children) => (
    <div className={`mt-3 flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 ${stalled ? 'bg-amber-50 border border-amber-300' : 'bg-slate-50'}`} data-testid={`stage-next-${p.id}`}>
      {p.halted ? <span className="text-xs font-bold text-amber-800" data-testid={`stage-halted-${p.id}`}>⛔ Parada — {p.halted.reason} (emails automáticos desligados)<HaltClear p={p} onChanged={onChanged} /> —</span>
        : stalled && <span className="text-xs font-bold text-amber-800" data-testid={`stage-stalled-${p.id}`}>⚠️ Parada há {daysInStage(p)} dias nesta etapa —</span>}
      <span className="text-xs font-semibold text-slate-500">Próximo passo:</span>{children}
    </div>
  );
  if (p.to_reschedule) return wrap(<><span className="text-xs text-rose-700 font-semibold" data-testid={`stage-to-reschedule-${p.id}`}>A remarcar — {p.to_reschedule.outcome === 'no_show' ? 'não compareceu' : p.to_reschedule.outcome === 'cancelled' ? 'cancelada pela empresa' : 'remarcar'}{p.to_reschedule.reason_text ? ` (${p.to_reschedule.reason_text})` : ''}</span><ProposeDateButton p={p} onChanged={onChanged} /><ArchiveRescheduleButton p={p} onChanged={onChanged} /></>);
  if (meetingPassed(p)) return wrap(<><MeetingTimes p={p} /><MeetingOutcome p={p} onChanged={onChanged} /></>);
  switch (stage) {
    case 'to_contact': return wrap(<Button size="sm" onClick={() => onCompose(p)} data-testid={`stage-action-compose-${p.id}`}>Escrever e enviar email</Button>);
    case 'contacted': return wrap(<span className="text-xs text-slate-600">Aguardar resposta (seguimento automático)</span>);
    case 'interested': return wrap(<ProspectReplyButton p={p} onSent={onChanged} />);
    case 'preference': return wrap(p.contact_preference?.channel !== 'whatsapp'
      ? <Button size="sm" onClick={clickInvite} data-testid={`stage-action-invite-${p.id}`}>Enviar convite</Button>
      : <span className="text-xs text-slate-600">Combinar a hora por WhatsApp (ver acima)</span>);
    case 'invite_sent': return wrap(p.meeting ? <><MeetingTimes p={p} /><ConfirmManualButton p={p} onChanged={onChanged} /></> : <span className="text-xs text-slate-600">Aguardar que a empresa escolha um dos horários propostos</span>);
    case 'confirmed': return wrap(<MeetingTimes p={p} />);
    case 'held': return wrap(<>{!p.contact_preference && <ProspectReplyButton p={p} onSent={onChanged} label="Enviar email de seguimento" />}<Button size="sm" variant="outline" onClick={() => navigate(`/admin/prospects/${p.id}/meetings`)} data-testid={`stage-action-report-${p.id}`}>📝 Escrever relatório</Button><PioneerAction p={p} onChanged={onChanged} /></>);
    case 'pioneer': return wrap(<span className="text-xs text-emerald-700">{LABEL.pioneer} — acompanhar a activação</span>);
    default: return null;
  }
};

export const StageHistory = ({ p }) => {
  const [open, setOpen] = useState(false);
  const events = (p.history || []).filter((h) => h.event?.startsWith('stage:'));
  if (!events.length) return null;
  return (
    <div className="mt-2 text-xs" data-testid={`stage-history-${p.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="text-slate-500 underline" data-testid={`stage-history-toggle-${p.id}`}>Histórico de etapas ({events.length})</button>
      {open && (
        <ol className="mt-1 space-y-0.5 border-l-2 border-slate-200 pl-3">
          {[...events].reverse().map((h, i) => (
            <li key={`${h.at}-${i}`} className="text-slate-600">{LABEL[h.event.slice(6)] || h.event.slice(6)}{h.detail?.includes('(manual)') ? ' · manual' : ''} — {prospectDate(h.at)?.toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</li>
          ))}
        </ol>
      )}
    </div>
  );
};
